/**
 * CategoryFeedPage — `/c/:slug` — shows all sellers tagged with a given
 * business category, plus the subcategory filter chips along the top.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import RestaurantCard from "../../components/RestaurantCard";
import BizCategoryIcon from "../../components/BizCategoryIcon";
import { CardSkeleton } from "../../components/Loaders";
import GroupCategoryChipRow from "../../components/GroupCategoryChipRow";
import BannerCarouselDynamic from "../../components/BannerCarouselDynamic";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import BkStoresView from "./blinkit/BkStoresView";

// Blinkit-theme tenants get the Blinkit stores list; every other theme keeps this page unchanged.
export default function CategoryFeedPage() {
    const { slug } = useParams();
    return useIsBlinkit() ? <BkStoresView key={slug} verticalSlug={slug} /> : <ClassicCategoryFeedPage />;
}

function ClassicCategoryFeedPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        // Cleared inside the load fn after the next response arrives;
        // the previous version set data=null synchronously here, but
        // doing that during effect mount triggers React 19's
        // set-state-in-effect rule. Showing the stale list briefly is
        // fine — the new data lands within a tick.
        (async () => {
            // Resolve the customer's saved-default location so the
            // backend can attach ``distance_km`` to every seller — see
            // bug #11. Falls back to a coord-less request for anonymous
            // visitors (distance just won't render).
            let q = "";
            try {
                const { data } = await api.get("/addresses");
                const def = (data || []).find((a) => a.is_default) || (data || [])[0];
                if (def?.lat != null && def?.lng != null) q = `?lat=${def.lat}&lng=${def.lng}`;
            } catch { /* anonymous */ }
            try {
                const { data } = await api.get(`/category/${slug}${q}`);
                if (!cancelled) setData(data);
            } catch {
                if (!cancelled) setData({ category: null, subcategories: [], sellers: [] });
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (!data) {
        return (
            <div className="p-4 space-y-4">
                <div className="h-8 w-40 skeleton rounded-md" />
                <CardSkeleton /><CardSkeleton />
            </div>
        );
    }
    if (!data.category) {
        return (
            <div
                className="flex flex-col items-center justify-center px-8 text-center text-muted-foreground"
                // iter146 — was a plain `p-8 text-center` block with no
                // height/centering at all, so it rendered pinned to the
                // TOP of the page (user-reported screenshot) instead of
                // filling the screen. Matches the same safe-area-aware
                // fill used for other empty states in this app (Cart,
                // Login) — accounts for what the ancestor `.pwa-shell`/
                // `.pwa-bg` wrappers (index.css) already consume via
                // `body`'s own safe-area padding, so this doesn't
                // independently re-claim a second full viewport and
                // overflow-scroll on short content.
                style={{ minHeight: "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px))" }}
            >
                <p>Category not found.</p>
                <Link to="/" className="text-primary font-semibold mt-3 inline-block">Back to home</Link>
            </div>
        );
    }

    // iter85 — the legacy ``subcategories`` chip row was rarely
    // populated (most tenants don't author child BusinessCategories) and
    // duplicated work the new Group Categories cover. Replaced by the
    // tenant-defined Group Categories row scoped to this vertical.
    const sellers = data.sellers || [];

    return (
        <div className="pb-32" data-testid="category-feed-page">
            <header className="sticky top-0 bg-background z-10 px-4 py-3 border-b border-border flex items-center gap-3">
                <button onClick={() => navigate(-1)} data-testid="cat-back" className="p-2 -ml-2 rounded-lg hover:bg-muted"><ArrowLeft size={20} /></button>
                <BizCategoryIcon name={data.category.icon} size={22} className="text-amber-500" strokeWidth={2.4} />
                <h1 className="text-lg font-bold">{data.category.name}</h1>
                <span className="ml-auto text-xs text-muted-foreground">{sellers.length} seller{sellers.length === 1 ? "" : "s"}</span>
            </header>

            {/* iter85 — Group Categories rail (replaces the old subcategory
                chips). Taps drill into ``/g/:slug`` for the unified
                group feed. ``GroupCategoryChipRow`` self-renders nothing
                when the tenant has no Group Categories mapped to this
                vertical, so the layout collapses cleanly for empty
                tenants. */}
            <GroupCategoryChipRow
                businessCategoryId={data.category.id}
                testIdPrefix="cat-group-chips"
            />

            {/* iter101 — placement="VERTICAL" banners scoped to this category. */}
            <BannerCarouselDynamic
                placement="VERTICAL"
                businessCategoryId={data.category.id}
                className="px-4 mt-3"
            />

            <section className="px-4 mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {sellers.length === 0 ? (
                    <div className="col-span-full bg-card border border-dashed border-border rounded-2xl p-8 text-center text-muted-foreground">
                        <p className="text-sm font-medium">No sellers in this category yet.</p>
                        <p className="text-xs mt-1">Check back soon — we&apos;re onboarding partners.</p>
                    </div>
                ) : (
                    sellers.map((s) => <RestaurantCard key={s.id} r={s} />)
                )}
            </section>
        </div>
    );
}
